import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Card } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaBars, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";
import axios from 'axios';
import Swal from 'sweetalert2';

const API_BASE_URL = 'https://backend.dlapakistan.com/api';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // Add timeout
});

const DentalLabJournalPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [journalCards, setJournalCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const headerRef = useRef(null);

  // Utility function to extract data from different API response structures
  const extractJournalCards = useCallback((responseData) => {
    if (!responseData) return [];
    
    // If it's already an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    // If it has a results property (paginated response)
    if (responseData.results && Array.isArray(responseData.results)) {
      return responseData.results;
    }
    
    // If it has a data property
    if (responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // If it has an items property
    if (responseData.items && Array.isArray(responseData.items)) {
      return responseData.items;
    }
    
    // If it's a single object, wrap in array
    if (typeof responseData === 'object' && responseData !== null && responseData.id) {
      return [responseData];
    }
    
    console.warn("Unexpected API response structure:", responseData);
    return [];
  }, []);

  // Fetch journal cards from backend
  useEffect(() => {
    let isMounted = true;
    
    const fetchJournalCards = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get('/dental-journal/cards/');
        console.log('API Response:', response.data);
        
        if (isMounted) {
          const extractedCards = extractJournalCards(response.data);
          setJournalCards(extractedCards);
          
          if (extractedCards.length === 0) {
            console.log('No journal cards found');
          }
        }
      } catch (err) {
        console.error('Fetch error:', err);
        if (isMounted) {
          setError(err.response?.data?.message || err.message || 'Failed to load journal cards');
          Swal.fire({
            title: 'Error',
            text: 'Failed to load journal cards',
            icon: 'error',
            timer: 3000,
            toast: true,
            position: 'top-end',
            showConfirmButton: false
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchJournalCards();
    
    return () => {
      isMounted = false;
    };
  }, [extractJournalCards]);

  useEffect(() => {
    // Header ki height detect karna
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') || 
                    document.querySelector('.header') ||
                    document.querySelector('#header');
      
      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        // Fallback height based on screen size
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else if (windowWidth < 1024) {
          setHeaderHeight(112);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
      if (window.innerWidth > 1024) {
        setIsSidebarOpen(false);
      }
    };

    // Initial setup
    updateHeaderHeight();
    window.addEventListener("resize", handleResize);
    
    // Resize observer for header changes
    const observer = new ResizeObserver(updateHeaderHeight);
    const headerElement = headerRef.current || 
                         document.querySelector('header');
    if (headerElement) observer.observe(headerElement);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (headerElement) observer.unobserve(headerElement);
    };
  }, [windowWidth]);

  const isMobileView = windowWidth <= 1024;

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  // Memoized helper functions
  const getImageUrl = useCallback((card) => {
    if (!card) return '/dentalab.png';
    
    // Check for different possible image fields
    return card.image_url || 
           card.image || 
           card.thumbnail_url || 
           card.thumbnail ||
           '/dentalab.png';
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  }, []);

  const searchTitleOnline = useCallback((title) => {
    if (!title) return;
    
    // Clean up the title for URL encoding
    const searchQuery = encodeURIComponent(`${title} dental journal article`);
    
    // Search on Google
    const googleSearchUrl = `https://www.google.com/search?q=${searchQuery}`;
    
    // Open in new tab
    window.open(googleSearchUrl, '_blank', 'noopener,noreferrer');
    
    // Show toast notification
    Swal.fire({
      title: 'Searching Online',
      text: `Searching for "${title.substring(0, 50)}${title.length > 50 ? '...' : ''}"`,
      icon: 'info',
      timer: 1500,
      showConfirmButton: false,
      toast: true,
      position: 'top-end'
    });
  }, []);

  // Memoized grid content to prevent unnecessary re-renders
  const gridContent = useMemo(() => {
    if (!journalCards || journalCards.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
            <svg className="w-8 h-8 text-[#70B444]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No journal issues yet
          </h3>
          <p className="text-gray-500 mb-6">
            Check back later for new journal issues
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 md:mt-8">
        {journalCards.map((card) => (
          <Card 
            key={card.id} 
            className="w-full h-auto border border-gray-200 rounded-lg overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow duration-300 group hover:border-[#70B444]"
          >
            {/* Image Container */}
            <div className="h-48 md:h-56 lg:h-80 w-full overflow-hidden relative bg-gray-100">
              <Card.Img 
                variant="top" 
                src={getImageUrl(card)} 
                alt={card.title || 'Journal issue'} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                onError={(e) => {
                  e.target.onerror = null; // Prevent infinite loop
                  e.target.src = '/dentalab.png';
                }}
                loading="lazy"
              />
              {card.created_at && (
                <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {formatDate(card.created_at)}
                </div>
              )}
            </div>

            {/* Card Body */}
            <Card.Body className="bg-white flex flex-col justify-between p-4">
              <div>
                {/* Title */}
                <h3 className="text-[#70B444] text-lg md:text-xl font-bold mb-2 leading-tight line-clamp-2 group-hover:text-[#5a9936] transition-colors">
                  {card.title || 'Untitled'}
                </h3>
                
                {/* Subtitle/Description */}
                {(card.subtitle || card.description) && (
                  <p className="text-gray-600 text-sm md:text-base mb-3 line-clamp-3">
                    {card.subtitle || card.description}
                  </p>
                )}
              </div>
              
              {/* View Issue Link */}
              <button
                onClick={() => searchTitleOnline(card.title)}
                className="text-[#70B444] font-bold text-sm md:text-base no-underline hover:underline inline-flex items-center gap-2 mt-2 hover:text-[#5a9936] transition-colors bg-transparent border-none cursor-pointer p-0"
                disabled={!card.title}
              >
                <span>Search article online</span>
                <FiExternalLink className="inline" />
              </button>
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  }, [journalCards, getImageUrl, formatDate, searchTitleOnline]);

  // Mobile overlay class
  const mobileOverlayClass = useMemo(() => 
    isMobileView && isSidebarOpen ? "pt-0" : "", 
    [isMobileView, isSidebarOpen]
  );

  return (
    <div 
      className={`w-full min-h-screen ${mobileOverlayClass}`}
      ref={headerRef}
    >
      {/* Main Container */}
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        {/* Breadcrumb section */}
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            The DentalLab Journal
          </span>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">

          {/* MAIN CONTENT AREA */}
          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                The DentalLab Journal
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                  aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444] mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading journal issues...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Something went wrong
                </h3>
                <p className="text-gray-500 mb-6">
                  {error}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-[#70B444] text-white rounded-lg hover:bg-[#5a9437] transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              gridContent
            )}
          </div>
      
          {/* Overlay */}
          {isMobileView && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              style={{ top: `${headerHeight}px` }}
              onClick={toggleSidebar}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DentalLabJournalPage;